import { 
  Exercise, 
  CreateExerciseInput, 
  UpdateExerciseInput,
  ExerciseType,
  MultipleChoiceData,
  FillInBlanksData,
  AudioRecordingData
} from './exercises.interfaces';

// In-memory store for mock data
const mockExercises: Record<string, Record<string, Exercise>> = {};
const mockLevelTestGroupExercises: Record<string, Record<string, Exercise>> = {};

// Mock implementation of the exercise service
export const createExerciseMock = async (input: CreateExerciseInput): Promise<Exercise> => {
  const id = `exercise-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  const exercise: Exercise = {
    ...input,
    id
  };

  // Create module bucket if it doesn't exist
  if (!mockExercises[input.moduleId]) {
    mockExercises[input.moduleId] = {};
  }
  
  // Store the exercise
  mockExercises[input.moduleId][id] = exercise;
  
  return exercise;
};

export const updateExerciseMock = async (exerciseId: string, input: UpdateExerciseInput): Promise<Exercise> => {
  // Find the exercise in all module buckets
  for (const moduleId in mockExercises) {
    if (mockExercises[moduleId][exerciseId]) {
      const exercise = mockExercises[moduleId][exerciseId];
      
      // Update the exercise
      mockExercises[moduleId][exerciseId] = {
        ...exercise,
        ...input
      };
      
      return mockExercises[moduleId][exerciseId];
    }
  }
  
  // If exercise not found, throw error
  throw new Error(`Exercise with ID ${exerciseId} not found`);
};

export const deleteExerciseMock = async (exerciseId: string): Promise<void> => {
  // Find the exercise in all module buckets
  for (const moduleId in mockExercises) {
    if (mockExercises[moduleId][exerciseId]) {
      // Delete the exercise
      delete mockExercises[moduleId][exerciseId];
      return;
    }
  }
  
  // If exercise not found, throw error
  throw new Error(`Exercise with ID ${exerciseId} not found`);
};

export const listExercisesByModuleMock = async (moduleId: string): Promise<Exercise[]> => {
  // Return exercises for the module, or empty array if none
  return Object.values(mockExercises[moduleId] || {});
};

// Helper function to generate mock exercises for a module
export const generateMockExercisesForModule = (moduleId: string, count: number = 3): void => {
  // Create module bucket if it doesn't exist
  if (!mockExercises[moduleId]) {
    mockExercises[moduleId] = {};
  }
  
  // Generate mock exercises
  for (let i = 0; i < count; i++) {
    const id = `exercise-mock-${i}`;
    let exercise: Exercise;
    
    // Create different types of exercises
    switch (i % 3) {
      case 0:
        // Multiple choice exercise
        exercise = {
          id,
          moduleId,
          type: ExerciseType.MultipleChoice,
          data: {
            question: `Sample multiple choice question ${i}?`,
            options: [
              { text: `Option A-${i}`, isCorrect: true, order: 0 },
              { text: `Option B-${i}`, isCorrect: false, order: 1 },
              { text: `Option C-${i}`, isCorrect: false, order: 2 }
            ],
            multipleCorrectAnswers: false
          } as MultipleChoiceData,
          order: i
        };
        break;
      case 1:
        // Fill in blanks exercise
        exercise = {
          id,
          moduleId,
          type: ExerciseType.FillInTheBlanks,
          data: {
            text: `This is a sample text with [blank] to fill in number ${i}.`,
            blanks: [`word-${i}`]
          } as FillInBlanksData,
          order: i
        };
        break;
      case 2:
      default:
        // Audio recording exercise
        exercise = {
          id,
          moduleId,
          type: ExerciseType.AudioRecording,
          data: {
            question: `Record this sentence in Portuguese: Sample sentence ${i}`,
            instructions: "Speak clearly into the microphone",
            maxDuration: 30
          } as AudioRecordingData,
          order: i
        };
        break;
    }
    
    // Store the exercise
    mockExercises[moduleId][id] = exercise;
  }
};

// Initialize some mock data
generateMockExercisesForModule('mock-module-1');
generateMockExercisesForModule('mock-module-2', 5);

// Level Test Group Exercise Mocks
export const listExercisesByLevelTestGroupMock = async (levelTestGroupId: string): Promise<Exercise[]> => {
  if (!mockLevelTestGroupExercises[levelTestGroupId]) {
    return [];
  }
  return Object.values(mockLevelTestGroupExercises[levelTestGroupId]);
};

export const createExerciseForLevelTestGroupMock = async (input: any): Promise<Exercise> => {
  const id = `ltg-exercise-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  const exercise: Exercise = {
    ...input,
    id,
    moduleId: input.levelTestGroupId // Use levelTestGroupId as moduleId for compatibility
  };

  if (!mockLevelTestGroupExercises[input.levelTestGroupId]) {
    mockLevelTestGroupExercises[input.levelTestGroupId] = {};
  }
  
  mockLevelTestGroupExercises[input.levelTestGroupId][id] = exercise;
  return exercise;
};

export const updateExerciseForLevelTestGroupMock = async (exerciseId: string, input: any): Promise<Exercise> => {
  for (const levelTestGroupId in mockLevelTestGroupExercises) {
    if (mockLevelTestGroupExercises[levelTestGroupId][exerciseId]) {
      const exercise = mockLevelTestGroupExercises[levelTestGroupId][exerciseId];
      mockLevelTestGroupExercises[levelTestGroupId][exerciseId] = {
        ...exercise,
        ...input
      };
      return mockLevelTestGroupExercises[levelTestGroupId][exerciseId];
    }
  }
  throw new Error(`Exercise with ID ${exerciseId} not found`);
};

export const deleteExerciseForLevelTestGroupMock = async (exerciseId: string, levelTestGroupId: string): Promise<void> => {
  if (mockLevelTestGroupExercises[levelTestGroupId] && mockLevelTestGroupExercises[levelTestGroupId][exerciseId]) {
    delete mockLevelTestGroupExercises[levelTestGroupId][exerciseId];
  } else {
    throw new Error(`Exercise with ID ${exerciseId} not found in level test group ${levelTestGroupId}`);
  }
};

// Initialize level test group exercises
const initializeLevelTestGroupExercises = () => {
  // Group 1 exercises (ltg-001)
  mockLevelTestGroupExercises['ltg-001'] = {
    'ex-1': {
      id: 'ex-1',
      moduleId: 'ltg-001',
      type: ExerciseType.MultipleChoice,
      data: {
        question: "What is the correct form of the verb 'to be' for 'I'?",
        options: [
          { text: "am", order: 1, isCorrect: true },
          { text: "is", order: 2, isCorrect: false },
          { text: "are", order: 3, isCorrect: false }
        ],
        multipleCorrectAnswers: false
      },
      order: 1,
      title: "Verb To Be - Basic"
    },
    'ex-2': {
      id: 'ex-2',
      moduleId: 'ltg-001',
      type: ExerciseType.MultipleChoice,
      data: {
        question: "Choose the correct article:",
        options: [
          { text: "a", order: 1, isCorrect: false },
          { text: "an", order: 2, isCorrect: true },
          { text: "the", order: 3, isCorrect: false }
        ],
        multipleCorrectAnswers: false
      },
      order: 2,
      title: "Articles - Basic"
    }
  };

  // Group 2 exercises (ltg-002)
  mockLevelTestGroupExercises['ltg-002'] = {
    'ex-3': {
      id: 'ex-3',
      moduleId: 'ltg-002',
      type: ExerciseType.MultipleChoice,
      data: {
        question: "What is the past tense of 'go'?",
        options: [
          { text: "goed", order: 1, isCorrect: false },
          { text: "went", order: 2, isCorrect: true },
          { text: "gone", order: 3, isCorrect: false }
        ],
        multipleCorrectAnswers: false
      },
      order: 1,
      title: "Past Tense - Irregular Verbs"
    }
  };
};

// Initialize the data
initializeLevelTestGroupExercises();